/* V25_APPLICATION_UI_HOLISTIC_FLOW_V1 */
import React, { useMemo, useState, useEffect } from "react";
import { capabilityField } from "../../../data/capabilityDetails";
import { analysis } from "../../../data/needs";
import { humanizeToken } from "./needsApplicationModel";
import ResearchExplanation from "./ResearchExplanation";
import BackendResearchExplanation from "./BackendResearchExplanation";
import { researchApi } from "../../../api/researchApi";

const MAIN_SHARED_LIMIT = 5;
const MAIN_SITUATED_LIMIT = 4;

function unique(values = []) {
  return [...new Set((values || []).filter(Boolean))];
}

function profileFor(capabilityId) {
  return (capabilityField?.capabilities || []).find(
    (item) => item.id === capabilityId
  );
}

function recurrenceMap() {
  const map = new Map();

  for (const capability of capabilityField?.capabilities || []) {
    for (const raw of unique(capability.conditions || [])) {
      map.set(raw, (map.get(raw) || 0) + 1);
    }
  }

  return map;
}

function buildStudyIndex() {
  const map = new Map();

  for (const field of analysis?.fields || []) {
    for (const source of field.sources || []) {
      if (!source?.root_id) continue;

      if (!map.has(source.root_id)) {
        map.set(source.root_id, {
          ...source,
          field_ids: [field.id],
          field_labels: [field.label],
        });
        continue;
      }

      const existing = map.get(source.root_id);
      existing.field_ids = unique([...(existing.field_ids || []), field.id]);
      existing.field_labels = unique([
        ...(existing.field_labels || []),
        field.label,
      ]);
      existing.capability_routes = [
        ...(existing.capability_routes || []),
        ...(source.capability_routes || []),
      ];
    }
  }

  return map;
}

function displayCondition(raw = "") {
  const simple = String(raw)
    .replace(/^agency:\s*/i, "")
    .replace(/^relational_safety:\s*/i, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!simple) return "Condition";
  if (simple.length > 72) return `${simple.slice(0, 69)}…`;

  return simple
    .split(" ")
    .map((token, index) =>
      index === 0
        ? token.charAt(0).toUpperCase() + token.slice(1)
        : token
    )
    .join(" ");
}

function sortConditions(items, recurrence) {
  return [...items].sort((a, b) => {
    const recurrenceDelta =
      (recurrence.get(b) || 1) - (recurrence.get(a) || 1);
    if (recurrenceDelta) return recurrenceDelta;
    return displayCondition(a).localeCompare(displayCondition(b));
  });
}

function ConditionNode({
  raw,
  count,
  kind,
  rootCount,
  onInspect,
}) {
  return (
    <button
      type="button"
      className={`v25a-v24-condition ${kind}`}
      onClick={() => onInspect(raw)}
    >
      <span className="v25a-v24-dot" aria-hidden="true" />
      <span>
        <b>{displayCondition(raw)}</b>
        <small>
          {count > 1
            ? `shared across ${count} capability profiles`
            : "situated in this capability profile"}
          {" · "}
          research context available
        </small>
      </span>
      <span className="v25a-v24-open">why? →</span>
    </button>
  );
}

function MoreConditionsNode({ count, onOpen }) {
  if (count <= 0) return null;

  return (
    <button
      type="button"
      className="v25a-v24-more-node"
      onClick={onOpen}
    >
      <span className="v25a-more-symbol">＋</span>
      <span>
        <b>{count} flere betingelser</b>
        <small>Zoom inn i betingelsesindeksen</small>
      </span>
      <span>zoom →</span>
    </button>
  );
}

function ConditionColumn({
  title,
  subtitle,
  items,
  visibleLimit,
  kind,
  recurrence,
  rootCount,
  onInspect,
  onOpenIndex,
}) {
  const sorted = sortConditions(items, recurrence);
  const visible = sorted.slice(0, visibleLimit);
  const remaining = Math.max(0, sorted.length - visible.length);

  return (
    <section className={`v25a-v24-column ${kind}`}>
      <div className="v25a-column-head">
        <span className="v25a-eyebrow">{title}</span>
        <small>{subtitle}</small>
      </div>

      <div className="v25a-v24-condition-stack">
        {visible.length ? (
          visible.map((raw) => (
            <ConditionNode
              key={raw}
              raw={raw}
              count={recurrence.get(raw) || 1}
              kind={kind}
              rootCount={rootCount}
              onInspect={onInspect}
            />
          ))
        ) : (
          <div className="v25a-empty-pathway">
            Ingen profilbetingelser i denne gruppen.
          </div>
        )}

        <MoreConditionsNode count={remaining} onOpen={onOpenIndex} />
      </div>
    </section>
  );
}

function RootCard({ root, rootId, capabilityId }) {
  const routes = (root?.capability_routes || []).filter(
    (route) => route.capability_id === capabilityId
  );

  return (
    <article className="v25a-root-card">
      <div className="v25a-root-card-top">
        <span>{rootId}</span>
        {root?.support_class ? <small>{root.support_class}</small> : null}
      </div>

      <b>{root?.title || "StudyRoot metadata not present in current compact handoff"}</b>

      <p>
        {(root?.authors || []).slice(0, 4).join(", ")}
        {root?.year ? ` · ${root.year}` : ""}
      </p>

      {routes.length ? (
        <div className="v25a-root-routes">
          {routes.slice(0, 3).map((route, index) => (
            <span key={`${route.capability_id}-${index}`}>
              {route.support_status || "route"} · {route.route || "capability route"}
            </span>
          ))}
        </div>
      ) : null}

      {root?.doi ? (
        <a
          href={`https://doi.org/${root.doi}`}
          target="_blank"
          rel="noreferrer"
        >
          original source ↗
        </a>
      ) : null}
    </article>
  );
}

function DrawerShell({ title, kicker, subtitle, onClose, children, onBack }) {
  return (
    <div className="v25a-drawer-layer">
      <button
        type="button"
        className="v25a-drawer-backdrop"
        aria-label="Lukk inspeksjon"
        onClick={onClose}
      />

      <aside className="v25a-inspect-drawer" aria-label="Capability inspection">
        <header className="v25a-drawer-head">
          <div>
            <span className="v25a-eyebrow">{kicker}</span>
            <h3>{title}</h3>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <div className="v25a-drawer-actions">
            {onBack ? (
              <button type="button" className="v25a-close" onClick={onBack}>
                ← Indeks
              </button>
            ) : null}
            <button type="button" className="v25a-close" onClick={onClose}>
              Lukk ×
            </button>
          </div>
        </header>

        {children}
      </aside>
    </div>
  );
}

function ConditionIndexDrawer({
  conditions,
  recurrence,
  onInspect,
  onClose,
}) {
  const sorted = sortConditions(conditions, recurrence);

  return (
    <DrawerShell
      kicker="ZOOM · CONDITION INDEX"
      title="Betingelser i capability-profilen"
      subtitle={`${sorted.length} profilbetingelser · velg én for å undersøke research-konteksten`}
      onClose={onClose}
    >
      <div className="v25a-grammar">
        <span>CAPABILITY PROFILE</span>
        <span>NOT MATERIALIZED PATHWAYS</span>
        <span>ORIENTATION NOT INFERRED</span>
      </div>

      <section className="v25a-condition-index-intro">
        <p>
          Hovedflaten viser bare et lite utvalg slik at kapabiliteten fortsatt
          er lesbar som helhet. Hele condition-reservoaret finnes her ett zoom
          dypere.
        </p>
      </section>

      <div className="v25a-condition-index-list">
        {sorted.map((raw) => (
          <button
            type="button"
            key={raw}
            onClick={() => onInspect(raw)}
          >
            <span>
              <b>{displayCondition(raw)}</b>
              <small>
                appears in {recurrence.get(raw) || 1} capability{" "}
                {(recurrence.get(raw) || 1) === 1 ? "profile" : "profiles"}
              </small>
            </span>
            <i>inspect →</i>
          </button>
        ))}
      </div>
    </DrawerShell>
  );
}

function ConditionInspectDrawer({ raw, profile, recurrence, studyIndex, onClose, onBack }) {
  const [backendPayload,setBackendPayload]=useState(null);
  const [backendLoading,setBackendLoading]=useState(true);
  const [backendError,setBackendError]=useState(null);
  const [showContext,setShowContext]=useState(false);
  const [showStudies,setShowStudies]=useState(false);

  useEffect(()=>{
    let alive=true;
    setBackendPayload(null); setBackendError(null); setBackendLoading(true); setShowContext(false); setShowStudies(false);
    researchApi.getCapabilityExplanation(profile.id)
      .then(x=>{if(alive){setBackendPayload(x);setBackendLoading(false)}})
      .catch(e=>{if(alive){setBackendError(e?.message||String(e));setBackendLoading(false)}});
    return()=>{alive=false};
  },[profile.id]);

  if(!raw||!profile)return null;
  const rootIds=unique([...(profile.core_roots||[]),...(profile.partial_roots||[])]);
  const roots=rootIds.map(rootId=>[rootId,studyIndex.get(rootId)||null]);

  return <DrawerShell kicker="INSPECT · CAPABILITY PROFILE CONDITION" title={displayCondition(raw)} subtitle={`Capability-profile context · appears in ${recurrence.get(raw)||1} profiles`} onClose={onClose} onBack={onBack}>
    <div className="v25a-grammar"><span>CAPABILITY PROFILE CONDITION</span><span>NOT A MATERIALIZED PATHWAY</span><span>CAUSALITY NOT CLAIMED</span></div>
    <BackendResearchExplanation mode="capability" payload={backendPayload} loading={backendLoading} error={backendError} capabilityLabel={profile.label} onOpenContext={()=>setShowContext(true)}/>
    {showContext?<section className="v25b-context"><div className="v25b-layer-head"><div><span className="v25a-eyebrow">BROADER CAPABILITY RESEARCH CONTEXT</span><b>Context only — not the pathway explanation</b></div><button onClick={()=>{setShowContext(false);setShowStudies(false)}}>Skjul context ↑</button></div><ResearchExplanation mode="v24" condition={displayCondition(raw)} capabilityId={profile.id} profile={profile} onOpenResearch={()=>setShowStudies(true)}/>{showStudies?<section className="v25a-v24-inspect-block v25a-studies-layer"><div className="v25a-research-section-head"><div><span className="v25a-eyebrow">STUDIES / STUDYROOTS · CONTEXT ONLY</span><b>{roots.length} studies in this capability profile</b></div><button className="v25a-collapse-studies" onClick={()=>setShowStudies(false)}>Skjul studier ↑</button></div><div className="v25a-root-card-list">{roots.map(([rootId,root])=><RootCard key={rootId} rootId={rootId} root={root} capabilityId={profile.id}/>)}</div></section>:null}</section>:null}
    <section className="v25a-v24-inspect-block"><span className="v25a-eyebrow">SCOPES</span><div className="v25a-v24-root-chips">{(profile.scopes||[]).map(scope=><span key={scope}>{scope}</span>)}</div></section>
  </DrawerShell>;
}

export default function V24ProfileCanvas({
  capabilityId,
  capabilityLabel,
  reason = "no-state",
}) {
  const profile = useMemo(() => profileFor(capabilityId), [capabilityId]);
  const recurrence = useMemo(() => recurrenceMap(), []);
  const studyIndex = useMemo(() => buildStudyIndex(), []);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedCondition, setSelectedCondition] = useState(null);

  if (!profile) {
    return (
      <section className="v25a-v24-profile v25a-v24-missing">
        <span className="v25a-eyebrow">V24 PROFILE</span>
        <h2>{capabilityLabel || humanizeToken(capabilityId)}</h2>
        <p>Ingen lokal V24 capability-profil ble funnet.</p>
      </section>
    );
  }

  const conditions = unique(profile.conditions || []);
  const shared = conditions.filter((raw) => (recurrence.get(raw) || 1) > 1);
  const situated = conditions.filter((raw) => (recurrence.get(raw) || 1) <= 1);
  const rootIds = unique([
    ...(profile.core_roots || []),
    ...(profile.partial_roots || []),
  ]);

  function inspect(raw) {
    setSelectedCondition(raw);
    setDrawerMode("condition");
  }

  function openIndex() {
    setDrawerMode("index");
  }

  function closeDrawer() {
    setDrawerMode(null);
    setSelectedCondition(null);
  }

  return (
    <section className="v25a-v24-profile">
      <header className="v25a-workspace-head">
        <div>
          <span className="v25a-eyebrow">CAPABILITY PROFILE · CONTEXT LAYER</span>
          <h1>{capabilityLabel || profile.label}</h1>
          <p>
            Samme holistiske arbeidsflate brukes på tvers av epistemiske
            dybder. Hovedbildet viser få betingelser; resten zoomes inn uten å
            forlate kapabiliteten.
          </p>
        </div>

        <div className="v25a-head-receipts">
          <span>CAPABILITY PROFILE</span>
          <span className="future">NO MATERIALIZED CONFIGURATION</span>
        </div>
      </header>

      <div className="v25a-v24-reason">
        <b>
          {reason === "unavailable"
            ? "Research Runtime er ikke tilgjengelig akkurat nå."
            : "Configuration topology er ikke materialisert for denne kapabiliteten."}
        </b>
        <span>
          Derfor klassifiserer UI-et ikke betingelser som inhibitor/enabler.
          Det ville vært en analytisk påstand som capability-profilen ikke har tjent.
        </span>
      </div>

      <div className="v25a-v24-canvas">
        <ConditionColumn
          title="SHARED PROFILE CONDITIONS"
          subtitle="Et lite representativt utsnitt · resten ett zoom dypere"
          items={shared}
          visibleLimit={MAIN_SHARED_LIMIT}
          kind="shared"
          recurrence={recurrence}
          rootCount={rootIds.length}
          onInspect={inspect}
          onOpenIndex={openIndex}
        />

        <div className="v25a-core-column">
          <article className="v25a-capability-core v24">
            <span className="v25a-eyebrow">STABILT SENTRUM · CAPABILITY</span>
            <h2>{capabilityLabel || profile.label}</h2>
            <p>
              Kapabiliteten forblir sentrum mens research, betingelser og
              senere materialisert topologi åpnes rundt den.
            </p>

            <div className="v25a-core-receipt">
              <span>
                <b>{rootIds.length}</b>
                research roots
              </span>
              <span>
                <b>{conditions.length}</b>
                profile conditions
              </span>
            </div>

            <button
              type="button"
              className="v25a-core-zoom-button"
              onClick={openIndex}
            >
              Utforsk alle betingelser →
            </button>

            <div className="v25a-v24-scope-row">
              {(profile.scopes || []).slice(0, 5).map((scope) => (
                <span key={scope}>{scope}</span>
              ))}
            </div>
          </article>

          <div className="v25a-center-caption">
            zoom = mer oppløsning · ikke ny side
          </div>
        </div>

        <ConditionColumn
          title="SITUATED / OTHER CONDITIONS"
          subtitle="Profilspesifikke betingelser"
          items={situated}
          visibleLimit={MAIN_SITUATED_LIMIT}
          kind="situated"
          recurrence={recurrence}
          rootCount={rootIds.length}
          onInspect={inspect}
          onOpenIndex={openIndex}
        />
      </div>

      <section className="v25a-v24-boundary-strip">
        <div>
          <span className="v25a-eyebrow">CURRENT EPISTEMIC DEPTH</span>
          <b>Capability profile</b>
        </div>

        <p>
          Vi viser research-kontekst og profile conditions. Affording /
          constraining orientation, convergence/divergence og ResultantField
          vises først når en materialisert konfigurasjonstilstand faktisk finnes.
        </p>
      </section>

      <footer className="v25a-workspace-foot">
        <div>
          <b>One capability, one workspace.</b>
          <span>
            Betingelser og forskning åpnes som flere lag av samme analyse —
            ikke som en serie nye sider.
          </span>
        </div>
      </footer>

      {drawerMode === "index" ? (
        <ConditionIndexDrawer
          conditions={conditions}
          recurrence={recurrence}
          onInspect={inspect}
          onClose={closeDrawer}
        />
      ) : null}

      {drawerMode === "condition" && selectedCondition ? (
        <ConditionInspectDrawer
          key={selectedCondition}
          raw={selectedCondition}
          profile={profile}
          recurrence={recurrence}
          studyIndex={studyIndex}
          onClose={closeDrawer}
          onBack={() => setDrawerMode("index")}
        />
      ) : null}
    </section>
  );
}
